type Options = Partial<AccordionOptions>;

export interface AccordionElement extends HTMLElement {
  accordion?: Accordion;
}

export interface AccordionOptions {
  activeClass: string;
  anchorSelector: string;
  itemSelector: string;
  listSelector: string;
}

export class Accordion {
  static defaults = {
    activeClass: 'is-active',
    anchorSelector: '[data-accordion-anchor]',
    itemSelector: '[data-accordion-item]',
    listSelector: '[data-accordion-list]',
  };

  clickHandler: EventListener;
  items: HTMLElement[];
  options: AccordionOptions;
  element: AccordionElement;

  constructor(element: AccordionElement, options: Options) {
    if (!element || element.accordion) {
      return;
    }
    element.accordion = this;
    this.element = element;
    this.options = {
      ...Accordion.defaults,
      ...this.element.dataset,
      ...options,
    };

    const items = Array.from(
      this.element.querySelectorAll(this.options.itemSelector),
    ) as HTMLElement[];

    if (items.length === 0) {
      return;
    }

    this.items = items;
    this.bind();
  }

  bind() {
    this.clickHandler = this.handleClick.bind(this);

    this.items.forEach(item => {
      const trigger = item.querySelector(this.options.anchorSelector);

      if (!trigger) {
        return;
      }

      trigger.addEventListener('click', this.clickHandler);
    });
  }

  handleClick(event: Event) {
    const target = event.target as HTMLElement;
    const match = target.closest(this.options.itemSelector) as HTMLElement;

    if (!match) {
      return;
    }

    this.toggle(match);
  }

  toggle(target: Element) {
    target.classList.toggle(this.options.activeClass);

    this.items.forEach(item => {
      if (item !== target) {
        item.classList.remove(this.options.activeClass);
      }
    });

    if (target.classList.contains(this.options.activeClass)) {
      target.scrollIntoView();
    }
  }

  destroy = () => {
    if (!this.element.accordion) {
      return;
    }

    this.element.accordion = undefined;

    if (this.items.length === 0) {
      return;
    }

    this.items.forEach(item => {
      const trigger = item.querySelector(this.options.anchorSelector);

      if (!trigger) {
        return;
      }

      trigger.removeEventListener('click', this.clickHandler);
    });
  };
}
